import React from 'react';
import { Link } from 'react-router-dom';

function TermsAndConditions() {
  return (
    <div className="theme-copy-page terms-page min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-gray-600">Last Updated: June 21, 2026</p>
          <p className="text-gray-700 mt-4">
            Please read these terms and conditions carefully before using DeRoyalty Car Rental services.
            By renting a vehicle from us, you agree to be bound by these terms.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Table of Contents</h2>
          <ul className="space-y-2">
            <li><a href="#rental-agreement" className="text-primary hover:underline">1. Rental Agreement</a></li>
            <li><a href="#driver-requirements" className="text-primary hover:underline">2. Driver Requirements</a></li>
            <li><a href="#vehicle-usage" className="text-primary hover:underline">3. Vehicle Usage</a></li>
            <li><a href="#insurance" className="text-primary hover:underline">4. Insurance Coverage</a></li>
            <li><a href="#payment" className="text-primary hover:underline">5. Payment Terms</a></li>
            <li><a href="#cancellation" className="text-primary hover:underline">6. Cancellation Policy</a></li>
            <li><a href="#liability" className="text-primary hover:underline">7. Liability and Damages</a></li>
            <li><a href="#prohibited" className="text-primary hover:underline">8. Prohibited Uses</a></li>
            <li><a href="#fuel" className="text-primary hover:underline">9. Fuel Policy</a></li>
            <li><a href="#return" className="text-primary hover:underline">10. Vehicle Return</a></li>
            <li><a href="#privacy" className="text-primary hover:underline">11. Privacy Policy</a></li>
            <li><a href="#contact" className="text-primary hover:underline">12. Contact Information</a></li>
          </ul>
        </div>

        {/* Section 1: Rental Agreement */}
        <div id="rental-agreement" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Rental Agreement</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              This rental agreement ("Agreement") is between DeRoyalty Car Rental ("Company", "we", "us")
              and the person signing this agreement ("Renter", "you"). By completing the rental process,
              you acknowledge that you have read, understood, and agree to be bound by all terms and conditions.
            </p>
            <p>
              The rental period begins when you take possession of the vehicle and ends when the vehicle
              is returned to our designated location and accepted by our staff.
            </p>
            <p>
              You agree to rent the vehicle in good condition and return it in the same condition,
              subject to normal wear and tear.
            </p>
          </div>
        </div>

        {/* Section 2: Driver Requirements */}
        <div id="driver-requirements" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Driver Requirements</h2>
          <div className="space-y-4 text-gray-700">
            <p className="font-semibold">To rent a vehicle from DeRoyalty Car Rental, you must:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Be at least 21 years of age (25 for luxury and premium vehicles)</li>
              <li>Hold a valid driver's license that has been valid for at least 1 year</li>
              <li>Present a valid government-issued photo ID</li>
              <li>Have a valid credit or debit card in your name</li>
              <li>Provide proof of insurance (if using personal insurance)</li>
              <li>Pass our verification and authorization process</li>
            </ul>
            <p className="mt-4">
              Additional drivers must meet the same requirements and must be present at the time of rental
              to be added to the agreement. Additional driver fees may apply.
            </p>
          </div>
        </div>

        {/* Section 3: Vehicle Usage */}
        <div id="vehicle-usage" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Vehicle Usage</h2>
          <div className="space-y-4 text-gray-700">
            <p>You agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the vehicle only for lawful purposes</li>
              <li>Operate the vehicle in accordance with all traffic laws and regulations</li>
              <li>Not allow anyone other than authorized drivers to operate the vehicle</li>
              <li>Keep the vehicle locked and keys secured when not in use</li>
              <li>Maintain proper fluid levels and tire pressure during the rental period</li>
              <li>Report any accidents, damage, or mechanical issues immediately</li>
              <li>Not modify or alter the vehicle in any way</li>
            </ul>
            <p className="mt-4">
              The vehicle may only be driven within the geographical area specified in your rental agreement.
              International travel requires prior written approval.
            </p>
          </div>
        </div>

        {/* Section 4: Insurance Coverage */}
        <div id="insurance" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Insurance Coverage</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              All rental vehicles include basic liability insurance as required by law. Additional coverage
              options are available:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Collision Damage Waiver (CDW):</strong> Limits your financial responsibility for damage to the rental vehicle</li>
              <li><strong>Theft Protection (TP):</strong> Reduces your liability if the vehicle is stolen</li>
              <li><strong>Personal Accident Insurance (PAI):</strong> Covers medical expenses for you and your passengers</li>
              <li><strong>Supplemental Liability Insurance (SLI):</strong> Additional liability coverage beyond basic limits</li>
            </ul>
            <p className="mt-4">
              You may decline our insurance if you have adequate coverage through your personal auto policy
              or credit card. You must provide proof of coverage if declining our insurance options.
            </p>
            <p>
              <strong>Important:</strong> Insurance may not cover damages resulting from prohibited uses,
              driving under the influence, or violations of this agreement.
            </p>
          </div>
        </div>

        {/* Section 5: Payment Terms */}
        <div id="payment" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Rental Charges:</strong> You agree to pay the daily rental rate plus applicable taxes,
              fees, and optional services selected at the time of booking.
            </p>
            <p>
              <strong>Security Deposit:</strong> A security deposit will be authorized on your credit/debit card
              at the start of the rental. The deposit amount varies based on vehicle type and rental duration.
            </p>
            <p>
              <strong>Payment Methods:</strong> We accept major credit cards (Visa, MasterCard, American Express)
              and debit cards. Cash payments may be accepted with additional requirements.
            </p>
            <p>
              <strong>Additional Charges:</strong> You will be charged for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Additional mileage beyond the included allowance</li>
              <li>Late return fees (charged per hour after grace period)</li>
              <li>Fuel charges if not returned with the same fuel level</li>
              <li>Cleaning fees for excessive dirt or odors</li>
              <li>Toll road charges and administrative fees</li>
              <li>Traffic violations or parking tickets</li>
              <li>Damage to the vehicle not covered by insurance</li>
            </ul>
          </div>
        </div>

        {/* Section 6: Cancellation Policy */}
        <div id="cancellation" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellation Policy</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Cancellation by Renter:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Free cancellation up to 48 hours before pickup time</li>
              <li>50% refund for cancellations 24-48 hours before pickup</li>
              <li>No refund for cancellations less than 24 hours before pickup</li>
              <li>No-show reservations forfeit all payments</li>
            </ul>
            <p className="mt-4">
              <strong>Modification of Reservations:</strong> You may modify your reservation (dates, vehicle type,
              pickup location) up to 24 hours before pickup without penalty, subject to availability and price changes.
            </p>
            <p>
              <strong>Early Returns:</strong> No refunds are provided for early returns. You will be charged
              for the full rental period reserved.
            </p>
            <p>
              <strong>Cancellation by Company:</strong> We reserve the right to cancel a reservation if payment
              authorization fails, driver requirements are not met, or the vehicle becomes unavailable due to
              unforeseen circumstances. Full refund will be provided in such cases.
            </p>
          </div>
        </div>

        {/* Section 7: Liability and Damages */}
        <div id="liability" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Liability and Damages</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Your Responsibility:</strong> You are responsible for all damage to or loss of the vehicle,
              including damage caused by weather, road conditions, acts of nature, or theft.
            </p>
            <p>
              <strong>Reporting Accidents:</strong> You must report all accidents, regardless of severity,
              to us and the police within 24 hours. Failure to report may void insurance coverage.
            </p>
            <p>
              <strong>Damage Assessment:</strong> Upon return, we will inspect the vehicle for damage.
              You will be charged for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Body damage, scratches, dents, or broken parts</li>
              <li>Interior damage, stains, burns, or tears</li>
              <li>Missing equipment, accessories, or keys</li>
              <li>Mechanical damage due to misuse or negligence</li>
              <li>Loss of use while the vehicle is being repaired</li>
              <li>Administrative fees for processing claims</li>
            </ul>
            <p className="mt-4">
              <strong>Limitation of Liability:</strong> We are not liable for any personal belongings left
              in the vehicle, delays, or consequential damages arising from vehicle breakdowns or unavailability.
            </p>
          </div>
        </div>

        {/* Section 8: Prohibited Uses */}
        <div id="prohibited" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Prohibited Uses</h2>
          <div className="space-y-4 text-gray-700">
            <p className="font-semibold text-red-600">The following uses are strictly prohibited and will void all coverage:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Operating the vehicle while under the influence of alcohol or drugs</li>
              <li>Using the vehicle for racing, towing, or pushing anything</li>
              <li>Operating the vehicle off-road or on unpaved roads</li>
              <li>Using the vehicle to carry passengers or property for hire</li>
              <li>Subletting or lending the vehicle to others</li>
              <li>Transporting hazardous or illegal materials</li>
              <li>Exceeding the vehicle's passenger or cargo capacity</li>
              <li>Driving recklessly or in violation of traffic laws</li>
              <li>Using the vehicle outside authorized geographical areas</li>
              <li>Smoking in non-smoking vehicles</li>
              <li>Transporting pets without prior authorization</li>
            </ul>
            <p className="mt-4 text-red-600 font-semibold">
              Violation of these prohibitions may result in immediate termination of the rental,
              forfeiture of all payments, and criminal prosecution where applicable.
            </p>
          </div>
        </div>

        {/* Section 9: Fuel Policy */}
        <div id="fuel" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Fuel Policy</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Full-to-Full Policy:</strong> Vehicles are provided with a full tank of fuel and must
              be returned with a full tank. If you return the vehicle without refueling, you will be charged
              for the missing fuel plus a refueling service fee.
            </p>
            <p>
              <strong>Fuel Type:</strong> You must use the correct fuel type as specified on the vehicle
              and in your rental agreement. Using incorrect fuel will result in charges for tank draining,
              engine repairs, and loss of use.
            </p>
            <p>
              <strong>Prepaid Fuel Option:</strong> You may purchase a full tank of fuel at the start of
              the rental at competitive rates, allowing you to return the vehicle without refueling.
              No refund is provided for unused fuel.
            </p>
          </div>
        </div>

        {/* Section 10: Vehicle Return */}
        <div id="return" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Vehicle Return</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Return Location:</strong> The vehicle must be returned to the location specified
              in your rental agreement. Different drop-off locations may incur additional fees.
            </p>
            <p>
              <strong>Return Time:</strong> The vehicle must be returned by the date and time specified
              in your agreement. A grace period of 29 minutes is provided. After the grace period,
              hourly late fees apply. Late returns beyond 2 hours will be charged an additional full day.
            </p>
            <p>
              <strong>After-Hours Return:</strong> If returning outside business hours, park the vehicle
              in our designated area, lock it, and place keys in the secure drop box. You remain responsible
              for the vehicle until it is inspected by our staff during business hours.
            </p>
            <p>
              <strong>Extension:</strong> If you need to extend your rental, contact us at least 24 hours
              before the original return time. Extensions are subject to vehicle availability and may be
              charged at different rates.
            </p>
          </div>
        </div>

        {/* Section 11: Privacy Policy */}
        <div id="privacy" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Privacy Policy</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Information Collection:</strong> We collect personal information including name,
              address, contact details, driver's license information, and payment information necessary
              to process your rental.
            </p>
            <p>
              <strong>Use of Information:</strong> Your information is used to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Process reservations and payments</li>
              <li>Verify identity and driver eligibility</li>
              <li>Communicate about your rental</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve our services and customer experience</li>
              <li>Send promotional materials (with your consent)</li>
            </ul>
            <p className="mt-4">
              <strong>Data Protection:</strong> We implement security measures to protect your personal
              information. We do not sell or share your information with third parties except as necessary
              to provide services (payment processing, insurance verification) or as required by law.
            </p>
            <p>
              <strong>Your Rights:</strong> You have the right to access, correct, or delete your personal
              information. Contact us to exercise these rights.
            </p>
          </div>
        </div>

        {/* Section 12: Contact Information */}
        <div id="contact" className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              If you have questions about these terms and conditions or need assistance with your rental,
              please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p className="font-semibold text-gray-900 mb-2">DeRoyalty Car Rental</p>
              <p>Email: info@deroyalty.com</p>
              <p>Phone: +1 (555) 123-4567</p>
              <p>Emergency Roadside Assistance: +1 (555) 999-8888 (24/7)</p>
              <p className="mt-4">
                Business Hours:<br />
                Monday - Friday: 8:00 AM - 8:00 PM<br />
                Saturday - Sunday: 9:00 AM - 6:00 PM
              </p>
            </div>
          </div>
        </div>

        {/* Legal Footer */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement Acceptance</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              By completing your rental reservation, you acknowledge that you have read, understood,
              and agree to be bound by these Terms and Conditions. These terms constitute the entire
              agreement between you and DeRoyalty Car Rental regarding your vehicle rental.
            </p>
            <p>
              We reserve the right to modify these terms at any time. Changes will be effective
              immediately upon posting on our website. Your continued use of our services after
              changes are posted constitutes acceptance of the modified terms.
            </p>
            <p className="font-semibold">
              These terms are governed by applicable local and federal laws. Any disputes will be
              resolved in accordance with the laws of the jurisdiction where the rental originates.
            </p>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
